import './CardForm.scss'

import { useCallback, useEffect, useState } from 'react'
import { Col, Form, FloatingLabel, Row } from 'react-bootstrap'

import * as validator from 'card-validator'

/**
 * Simple CardForm component. Used to collect credit card information
 */
export default function CardForm({ onChange }) {
  const [firstName, setFirstName] = useState()
  const [lastName, setLastName] = useState()
  const [zip, setZip] = useState()
  const [cardNumber, setCardNumber] = useState()
  const [cardVerification, setCardVerification] = useState()
  const [cardExpiration, setCardExpiration] = useState()
  const [cardDetails, setCardDetails] = useState()
  const [cardCVVName, setCardCVVName] = useState('CVV')

  const setName = name => {
    const names = name.split(' ') // Split on the space
    const first = names.shift() // Shift out the first element
    const last = names.join(' ') // Recombine the remaining items

    setFirstName(first)
    setLastName(last)
  }

  const isValidName = name => {
    const valid = validator.cardholderName(name)
    return valid.isValid
  }

  const isPotentiallyValidCardNumber = number => {
    const valid = validator.number(number)
    return valid.isPotentiallyValid
  }

  const isValidCardNumber = number => {
    const valid = validator.number(number)
    return valid.isPotentiallyValid && valid.isValid
  }

  const isValidExpiration = expiration => {
    const valid = validator.expirationDate(expiration)
    return valid.isValid
  }

  const isValidVerification = verification => {
    const valid = validator.cvv(verification)
    return valid.isValid
  }

  const isValidZip = zipcode => {
    const valid = validator.postalCode(zipcode)
    return valid.isValid
  }

  const isValidCard = useCallback(
    () =>
      isValidName(`${firstName} ${lastName}`) &&
      isValidCardNumber(cardNumber) &&
      isValidExpiration(cardExpiration) &&
      isValidVerification(cardVerification) &&
      isValidZip(zip),
    [firstName, lastName, cardNumber, cardVerification, cardExpiration, zip]
  )

  const getLogoPath = () => {
    return cardDetails && `/logos/${cardDetails?.card?.type}.svg`
  }

  useEffect(() => {
    const card = {
      firstName,
      lastName,
      cardNumber,
      cardVerification,
      cardExpiration,
      zip,
      isValid: isValidCard() // Is everything valid?
    }
    const details = validator.number(cardNumber)
    const cvvName = details?.card?.code?.name || cardCVVName

    setCardDetails(details)
    setCardCVVName(cvvName)
    onChange(card)
  }, [
    firstName,
    lastName,
    cardNumber,
    cardCVVName,
    cardVerification,
    cardExpiration,
    zip,
    onChange,
    isValidCard
  ])

  return (
    <div className='text-left card-form'>
      <Row>
        {/* Name */}
        <Form.Group className='form-group mb-2' controlId='formName'>
          <FloatingLabel controlId='floatingName' label='Name on Card'>
            <Form.Control
              name='name'
              size='lg'
              placeholder='Name on Card'
              required
              type='text'
              onChange={e => setName(e.currentTarget.value)}
              isInvalid={firstName && !firstName.trim().length}
              isValid={firstName && firstName.trim().length}
            />
            <Form.Control.Feedback type='invalid'>
              Please provide your name
            </Form.Control.Feedback>
          </FloatingLabel>
        </Form.Group>
      </Row>
      <Row>
        {/* Credit Card Number */}
        <Form.Group className='form-group mb-2' controlId='formCardNumber'>
          <FloatingLabel controlId='floatingCardNumber' label='Card Number'>
            <div className='card-logo'>
              <img src={getLogoPath()} width='43' alt='' />
            </div>
            <Form.Control
              name='cardNumber'
              size='lg'
              placeholder='Card Number'
              maxLength='20'
              pattern='[0-9]{16,20}'
              required
              type='text'
              onChange={e => setCardNumber(e.currentTarget.value)}
              isInvalid={
                cardNumber && !isPotentiallyValidCardNumber(cardNumber)
              }
              isValid={cardNumber && isValidCardNumber(cardNumber)}
            />
            <Form.Control.Feedback type='invalid'>
              Please provide a valid credit card number
            </Form.Control.Feedback>
          </FloatingLabel>
        </Form.Group>
      </Row>
      <Row>
        {/* Credit Card Expiration */}
        <Form.Group
          as={Col}
          xs={7}
          className='form-group mb-2'
          controlId='formCardExpiration'
        >
          <FloatingLabel
            controlId='floatingCardExpiration'
            label='Expiration (MMYY)'
          >
            <Form.Control
              name='cardExpiration'
              size='lg'
              placeholder='Expiration (MMYY)'
              maxLength='4'
              pattern='[0-9]{4}'
              required
              type='text'
              onChange={e => setCardExpiration(e.currentTarget.value)}
              isInvalid={cardExpiration && !isValidExpiration(cardExpiration)}
              isValid={cardExpiration && isValidExpiration(cardExpiration)}
            />
            <Form.Control.Feedback type='invalid'>
              Please provide a valid expiration
            </Form.Control.Feedback>
          </FloatingLabel>
        </Form.Group>

        {/* Credit Card Verification */}
        <Form.Group
          as={Col}
          className='form-group mb-2'
          controlId='formCardVerification'
        >
          <FloatingLabel
            controlId='floatingCardExpiration'
            label={`${cardCVVName} #`}
          >
            <Form.Control
              name='cardCVV'
              size='lg'
              placeholder={`${cardCVVName} #`}
              maxLength='4'
              required
              pattern='[0-9]{4}'
              type='text'
              onChange={e => setCardVerification(e.currentTarget.value)}
              isInvalid={
                cardVerification && !isValidVerification(cardVerification)
              }
              isValid={
                cardVerification && isValidVerification(cardVerification)
              }
            />
            <Form.Control.Feedback type='invalid'>
              Please provide a valid {cardCVVName} #
            </Form.Control.Feedback>
            <Form.Control.Feedback>All set!</Form.Control.Feedback>
          </FloatingLabel>
        </Form.Group>
      </Row>

      <Row className='mb-3'>
        {/* Zipcode */}
        <Form.Group className='form-group mb-2' controlId='formZip'>
          <FloatingLabel controlId='floatingZip' label='Zip/Postal code'>
            <Form.Control
              name='zip'
              size='lg'
              placeholder='Zip/Postal code'
              required
              type='text'
              onChange={e => setZip(e.currentTarget.value)}
              isInvalid={zip && !isValidZip(zip)}
              isValid={zip && isValidZip(zip)}
            />
            <Form.Control.Feedback type='invalid'>
              Please provide a valid zip or postal code
            </Form.Control.Feedback>
          </FloatingLabel>
        </Form.Group>
      </Row>
    </div>
  )
}
